import { useCallback, useRef } from 'react'

import { Transition } from '@headlessui/react'
import { IconXmarkLight, Select } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router'
import { useOnClickOutside } from 'usehooks-ts'

import { ConsumerSideNavigation } from '../ConsumerSideNavigation'
import { RootOutletContext } from '~/portal/types/portal'
import { LandingProduct } from '~/portal/types/product'
import { useAnimatedPanel } from '~/shared/hooks/useAnimatedPanel'

export const ConsumerSideNavigationMobile = ({ tableOfContents, accentColor, currentProduct, products }) => {

  const { t } = useTranslation()

  const ref = useRef(null) as unknown as React.RefObject<HTMLDivElement>
  
  const navigate = useNavigate()
  
  const { sideNavOpen, toggleSideNavWithTransition, closeSideNavWithoutTransition, instantSideNavClose } = useOutletContext<RootOutletContext>()
  
  useOnClickOutside(ref, () => toggleSideNavWithTransition?.(false))

  const onChangeProduct = useCallback((product: LandingProduct) => {
    closeSideNavWithoutTransition?.()
    navigate(`/${product.slug}`)
  }, [])

  const { isMounted, shouldAnimateIn } = useAnimatedPanel(!!sideNavOpen)

  if (instantSideNavClose) {
    return null
  }

  return (
    <Transition show={isMounted}>
      <div className="fixed inset-0 z-50 flex">
        <div
          className={clsx(
            'fixed inset-0 bg-(--sp-color-overlay) transition-opacity duration-400',
            shouldAnimateIn ? 'opacity-100' : 'opacity-0'
          )}
        />
        <nav
          className={clsx(
            'sp-sidenav-mobile relative z-50 w-[400px] h-full overflow-hidden xl:hidden bg-(--sp-sidenav-item-bg,var(--sp-color-surface)) max-w-[100vw]',
            tableOfContents?.length && 'border-r border-r-(--sp-color-border)',
            'transform transition-transform duration-400 ease-in-out',
            shouldAnimateIn ? 'translate-x-0' : '-translate-x-full'
          )}
          data-testid="responsive side-nav"
          ref={ref}
        >
          <section className="sp-sidenav-header flex h-[90px] p-6 items-center justify-between border-b border-b-(--sp-color-border)">
            {products?.length && currentProduct?.slug && (
              <div className="sp-sidenav-product-selector">
                <Select
                  name="product"
                  accentColor={accentColor}
                  className="w-[264px]"
                  buttonClassName="sp-sidenav-product-selector-button"
                  optionsClassName="sp-sidenav-product-selector-options w-[264px] !max-h-[240px] rounded border border-[#D8DDE4] dark:border-none shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                  optionClassName="!whitespace-pre-wrap !break-all"
                  theme="neutral"
                  defaultValue={currentProduct}
                  options={products}
                  onChange={onChangeProduct}
                  aria-label={t('consumer.topBar.productSelect.ariaLabel')}
                  size="m"
                />
              </div>
            )}
            <div
              onClick={() => { toggleSideNavWithTransition?.(false) }}
              className="cursor-pointer text-(--sp-color-text-muted) w-[44px] h-[44px] content-center"
              aria-label={t('consumer.edit.buttons.topBar.sideMenu.closeButton.ariaLabel')}
            >
              <IconXmarkLight width={20} height={20} className="m-auto" />
            </div>
          </section>
          <div className="h-[calc(100%_-_90px)] w-full">
            <ConsumerSideNavigation
              tableOfContents={tableOfContents}
              accentColor={accentColor}
            />
          </div>
        </nav>
      </div>
    </Transition>
  )
}